import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Upload, X, CheckCircle, Camera } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface FixRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  memorialId: string;
  monumentCompanyId: string;
  memorialName?: string;
}

export const FixRequestDialog: React.FC<FixRequestDialogProps> = ({
  open,
  onOpenChange,
  memorialId,
  monumentCompanyId,
  memorialName,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    description: "",
  });
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  
  // Camera state
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Stop camera when dialog closes
  useEffect(() => {
    if (!open) {
      stopCamera();
    }
  }, [open]);

  // Attach stream to video element AFTER it mounts (fixes black screen)
  useEffect(() => {
    if (isCameraActive && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch((err) => {
        console.error('[FixRequestDialog] Video play error:', err);
        setCameraError('Could not start video preview. Please use "Upload photo" instead.');
        stopCamera();
      });
    }
  }, [isCameraActive]);

  const startCamera = async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false,
      });
      streamRef.current = stream;
      // Set isCameraActive AFTER storing stream - useEffect will attach it
      setIsCameraActive(true);
    } catch (err) {
      console.error('[FixRequestDialog] Camera error:', err);
      setCameraError('Could not access camera. Please use "Upload photo" instead.');
      setIsCameraActive(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
      videoRef.current.pause();
    }
    setIsCameraActive(false);
    setCameraError(null);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (blob) {
          const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
          setPhotoFile(file);
          setPhotoPreview(canvas.toDataURL('image/jpeg'));
          stopCamera();
        }
      },
      'image/jpeg',
      0.9
    );
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const resetForm = () => {
    setFormData({
      customerName: "",
      customerEmail: "",
      customerPhone: "",
      description: "",
    });
    setPhotoFile(null);
    setPhotoPreview(null);
    setIsSuccess(false);
    stopCamera();
  };

  const handleClose = () => {
    stopCamera();
    onOpenChange(false);
    // Reset form after dialog closes
    setTimeout(resetForm, 300);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.customerName || !formData.customerEmail || !formData.description) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      let photoUrl: string | null = null;

      // Upload photo if provided
      if (photoFile) {
        const fileExt = photoFile.name.split('.').pop();
        const fileName = `fix-requests/${memorialId}/${Date.now()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('memorials')
          .upload(fileName, photoFile);

        if (uploadError) {
          console.error("Photo upload error:", uploadError);
          // Still proceed with the request even if photo upload fails
          toast.error("Photo upload failed, but request will be submitted without photo");
        } else {
          const { data: urlData } = supabase.storage
            .from('memorials')
            .getPublicUrl(fileName);
          photoUrl = urlData.publicUrl;
          console.log("Photo uploaded successfully:", photoUrl);
        }
      }

      // Insert fix request and get the ID back
      const { data: insertedRow, error } = await supabase
        .from('fix_requests')
        .insert({
          memorial_id: memorialId,
          monument_company_id: monumentCompanyId,
          customer_name: formData.customerName,
          customer_email: formData.customerEmail,
          customer_phone: formData.customerPhone || null,
          description: formData.description,
          photo_url: photoUrl,
        })
        .select('id')
        .single();

      if (error) throw error;

      // Send notification via dedicated edge function (works for guest users)
      try {
        await supabase.functions.invoke('notify-fix-request', {
          body: { fix_request_id: insertedRow.id },
        });
      } catch (emailError) {
        // Don't block the main flow if email fails
        console.error('Failed to send notification email:', emailError);
      }

      setIsSuccess(true);
    } catch (error) {
      console.error("Error submitting fix request:", error);
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col overflow-hidden">
        {isSuccess ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Request Submitted</h3>
            <p className="text-muted-foreground mb-6">
              Your issue has been reported. The monument company will review your request and contact you soon.
            </p>
            <Button onClick={handleClose}>Close</Button>
          </div>
        ) : (
          <>
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>Report an Issue</DialogTitle>
              <DialogDescription>
                {memorialName 
                  ? `Report a problem with ${memorialName}'s headstone or monument.`
                  : "Report a problem with this headstone or monument."
                }
              </DialogDescription>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto min-h-0 px-1">
              <form onSubmit={handleSubmit} className="space-y-4 mt-4 pb-4">
              <div className="space-y-2">
                <Label htmlFor="customerName">Your Name <span className="required-asterisk">*</span></Label>
                <Input
                  id="customerName"
                  value={formData.customerName}
                  onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                  placeholder="Enter your name"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="customerEmail">Email Address <span className="required-asterisk">*</span></Label>
                <Input
                  id="customerEmail"
                  type="email"
                  value={formData.customerEmail}
                  onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                  placeholder="Enter your email"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="customerPhone">Phone Number (optional)</Label>
                <Input
                  id="customerPhone"
                  type="tel"
                  value={formData.customerPhone}
                  onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
                  placeholder="Enter your phone number"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Describe the Issue <span className="required-asterisk">*</span></Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Please describe the issue you'd like to report..."
                  rows={4}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Photo (optional)</Label>
                
                {/* Camera Mode */}
                {isCameraActive ? (
                  <div className="space-y-3">
                    <div className="relative w-full max-h-48 aspect-[4/3] rounded-lg overflow-hidden bg-black">
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={stopCamera}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        onClick={capturePhoto}
                        className="flex-1"
                      >
                        <Camera className="w-4 h-4 mr-2" />
                        Capture
                      </Button>
                    </div>
                  </div>
                ) : photoPreview ? (
                  <div className="relative w-full h-32 max-h-48 rounded-lg overflow-hidden border">
                    <img
                      src={photoPreview}
                      alt="Issue preview"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={removePhoto}
                      className="absolute top-2 right-2 p-1 bg-black/50 rounded-full hover:bg-black/70"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {cameraError && (
                      <p className="text-sm text-destructive">{cameraError}</p>
                    )}
                    <div className="flex gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1 h-20 flex-col gap-1"
                        onClick={startCamera}
                      >
                        <Camera className="w-6 h-6" />
                        <span className="text-sm">Take photo</span>
                      </Button>
                      
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1 h-20 flex-col gap-1"
                        onClick={() => {
                          if (uploadInputRef.current) {
                            uploadInputRef.current.value = '';
                            uploadInputRef.current.click();
                          }
                        }}
                      >
                        <Upload className="w-6 h-6" />
                        <span className="text-sm">Upload photo</span>
                      </Button>
                      
                      {/* Hidden file input for upload */}
                      <input
                        ref={uploadInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoChange}
                        className="sr-only"
                      />
                    </div>
                  </div>
                )}
                
                {/* Hidden canvas for capturing photos */}
                <canvas ref={canvasRef} className="hidden" />
              </div>
              </form>
            </div>

            <div className="flex gap-3 pt-4 border-t flex-shrink-0">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={isSubmitting}
                onClick={handleSubmit}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit Request"
                )}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
